import { useState, useEffect, useRef } from 'react'
import { ArrowRight, AlertCircle } from 'lucide-react'
import { useAssessment } from '../../context/AssessmentContext'
import { getAllDASSQuestionsPart2 } from '../../data/dassQuestionsPart2'
import { profileQuestions, validateProfileAnswer } from '../../data/profileQuestions'
import InvestorMetricsEngine from '../../services/InvestorMetricsEngine'
import Shell from '../ui/Shell'
import AnswerRows from '../ui/AnswerRows'
import { computeDassScores } from '../../lib/flow'

// Short numeric hints; the unit is shown beside the field.
const PLACEHOLDER = { height: '170', weight: '70', sleep_hours: '7' }

const DASS_ANSWERS = [
  { title: 'Did not apply to me at all' },
  { title: 'Applied to me some of the time' },
  { title: 'Applied to me a good part of the time' },
  { title: 'Applied to me most of the time' },
]

function ExtendedAssessment() {
  const { state, dispatch } = useAssessment()
  const phase = state.extendedPhase
  const dassQs = getAllDASSQuestionsPart2()
  const total = dassQs.length + profileQuestions.length

  const isDass = phase === 'dass'
  const question = isDass ? dassQs[state.currentPart2DASSIndex] : profileQuestions[state.currentProfileIndex]
  const position = isDass ? state.currentPart2DASSIndex : dassQs.length + state.currentProfileIndex

  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    setDraft(question ? (state.profileAnswers[question.id] ?? '') : '')
    setError('')
    if (question?.type === 'number') inputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id])

  if (!question) {
    dispatch({ type: 'SET_SCREEN', payload: 'results' })
    return null
  }

  const finish = (profileAnswers) => {
    // Full 21-item DASS now that Part 2 is answered; metrics re-run with lifestyle data.
    const dassScores = computeDassScores(state.dassAnswers, state.part2DassAnswers)
    const engine = new InvestorMetricsEngine(state.addictionResults, dassScores, state.userAge || 35, profileAnswers)
    dispatch({ type: 'SET_DASS_SCORES', payload: dassScores })
    dispatch({ type: 'SET_INVESTOR_METRICS', payload: engine.generateAllMetrics() })
    dispatch({ type: 'COMPLETE_EXTENDED_ASSESSMENT' })
    dispatch({ type: 'SET_SCREEN', payload: 'results' })
  }

  const answerDass = (answerIndex) => {
    dispatch({ type: 'ANSWER_PART2_DASS_QUESTION', payload: { questionId: question.id, answerIndex } })
    if (state.currentPart2DASSIndex < dassQs.length - 1) dispatch({ type: 'NEXT_PART2_DASS_QUESTION' })
    else dispatch({ type: 'SET_EXTENDED_PHASE', payload: 'profile' })
  }

  const saveProfile = (value) => {
    const answers = { ...state.profileAnswers, [question.id]: value }
    dispatch({ type: 'ANSWER_PROFILE_QUESTION', payload: { questionId: question.id, answer: value } })
    if (state.currentProfileIndex < profileQuestions.length - 1) dispatch({ type: 'NEXT_PROFILE_QUESTION' })
    else finish(answers)
  }

  const submitNumber = (e) => {
    e.preventDefault()
    const v = validateProfileAnswer(question.id, draft)
    if (!v.valid) { setError(v.error); inputRef.current?.focus(); return }
    saveProfile(draft)
  }

  const skip = () => saveProfile('')

  const back = () => {
    if (isDass) {
      if (state.currentPart2DASSIndex > 0) dispatch({ type: 'PREV_PART2_DASS_QUESTION' })
      else dispatch({ type: 'SET_SCREEN', payload: 'results' })
    } else if (state.currentProfileIndex > 0) {
      dispatch({ type: 'PREV_PROFILE_QUESTION' })
    } else {
      dispatch({ type: 'SET_EXTENDED_PHASE', payload: 'dass' })
    }
  }

  const label = isDass ? 'Full assessment · Past week' : `Full assessment · ${question.category === 'demographics' ? 'About you' : question.category === 'health' ? 'Health' : 'Lifestyle'}`

  return (
    <Shell label={label} progress={(position + 1) / total} onBack={back}>
      <main className="flow">
        <div className="q" key={question.id}>
          <div className="q__meta">
            <span>Question {position + 1} of {total}</span>
            {isDass && <span>Past 7 days</span>}
            {!isDass && question.optional && <span>Optional</span>}
          </div>

          {isDass ? (
            <>
              <div className="q__body">
                <p className="q__frame">Over the past week…</p>
                <h1 className="q__text" style={{ marginTop: 8 }}>{question.question}</h1>
              </div>
              <div className="q__body" style={{ animationDelay: '40ms' }}>
                <AnswerRows options={DASS_ANSWERS} value={state.part2DassAnswers[question.id] ?? null} onChoose={answerDass} />
              </div>
              <p className="q__hint">How much did this apply to you?</p>
            </>
          ) : (
            <>
              <div className="q__body">
                <h1 className="q__text">{question.question}</h1>
                {question.description && <p className="q__hint" style={{ marginTop: 10 }}>{question.description}</p>}
              </div>

              {question.type === 'number' && (
                <>
                  <form id="pq-form" className="q__body" style={{ animationDelay: '40ms' }} onSubmit={submitNumber} noValidate>
                    <label htmlFor="pq" className="visually-hidden">{question.question}</label>
                    <div className="input-row">
                      <input
                        ref={inputRef}
                        id="pq"
                        type="number"
                        inputMode="decimal"
                        className="input input--num"
                        placeholder={PLACEHOLDER[question.id] || ''}
                        min={question.min}
                        max={question.max}
                        step={question.step || 1}
                        value={draft}
                        aria-invalid={error ? 'true' : 'false'}
                        onChange={(e) => { setDraft(e.target.value); if (error) setError('') }}
                      />
                      {question.unit && <span className="input-row__unit">{question.unit}</span>}
                    </div>
                    {error && (
                      <p className="error" role="alert"><AlertCircle size={16} strokeWidth={2} />{error}</p>
                    )}
                  </form>
                  {/* Outside the animated body: position:fixed needs the viewport as its containing block */}
                  <div className="flow__actions">
                    <button type="submit" form="pq-form" className="btn btn--block" disabled={!draft}>
                      {state.currentProfileIndex < profileQuestions.length - 1 ? 'Continue' : 'Finish'}
                      <ArrowRight size={18} strokeWidth={2.25} />
                    </button>
                    {question.optional && (
                      <button type="button" className="btn btn--secondary btn--block" onClick={skip}>Skip this one</button>
                    )}
                  </div>
                </>
              )}

              {question.type === 'single-choice' && (
                <div className="q__body" style={{ animationDelay: '40ms' }}>
                  <AnswerRows
                    options={question.options.map((o) => ({ key: o.value, title: o.text }))}
                    value={(() => { const i = question.options.findIndex((o) => o.value === state.profileAnswers[question.id]); return i === -1 ? null : i })()}
                    onChoose={(i) => saveProfile(question.options[i].value)}
                  />
                </div>
              )}

              {question.type === 'rating' && (
                <div className="q__body rating" style={{ animationDelay: '40ms' }} role="group" aria-label={question.question}>
                  {Array.from({ length: question.scale.max - question.scale.min + 1 }, (_, i) => {
                    const value = question.scale.min + i
                    const on = String(state.profileAnswers[question.id]) === String(value)
                    return (
                      <button
                        key={value}
                        type="button"
                        className="rating__btn"
                        aria-pressed={on}
                        onClick={() => window.setTimeout(() => saveProfile(String(value)), 140)}
                      >
                        <span className="rating__num">{value}</span>
                        <span className="rating__label">{question.scale.labels[value]}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </Shell>
  )
}

export default ExtendedAssessment
